/* eslint-disable react/prop-types */
import React from 'react';
import { useReactTable, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel } from '@tanstack/react-table';
import IndeterminateCheckbox from '../components/common/advance-table/IndeterminateCheckbox';

const EMPTY_TABLE_DATA = [];
const defaultGetRowId = (row, index) => row._id ?? row.id ?? String(index);

const selectionColumn = (selectionColumnWidth, selectionHeaderClassname) => {
	return {
		id: 'selection',
		accessorKey: '',
		header: ({ table }) => (
			<IndeterminateCheckbox
				className='form-check mb-0'
				{...{
					checked: table.getIsAllRowsSelected(),
					indeterminate: table.getIsSomeRowsSelected(),
					onChange: table.getToggleAllRowsSelectedHandler(),
				}}
			/>
		),
		cell: ({ row }) => (
			<IndeterminateCheckbox
				className='form-check mb-0'
				{...{
					checked: row.getIsSelected(),
					disabled: !row.getCanSelect(),
					indeterminate: row.getIsSomeSelected(),
					onChange: row.getToggleSelectedHandler(),
				}}
			/>
		),
		meta: {
			headerProps: {
				className: selectionHeaderClassname,
				style: {
					width: selectionColumnWidth,
				},
			},
			cellProps: {
				style: {
					width: selectionColumnWidth,
				},
			},
		},
	};
};

const useAdvanceTable = ({
	columns,
	data,
	sortable,
	selection,
	selectionColumnWidth,
	selectionHeaderClassname,
	pagination,
	initialState,
	perPage = 10,
	getRowId,
	enableRowSelection,
	meta,
	manualPagination = false,
	manualFiltering = false,
	pageCount,
	rowCount,
	state: controlledState,
	onPaginationChange,
	autoResetPageIndex,
}) => {
	const tableData = Array.isArray(data) && data.length > 0 ? data : EMPTY_TABLE_DATA;
	const state = {
		...initialState,
		pagination: {
			pageSize: pagination ? perPage : tableData.length,
			...(initialState?.pagination || {}),
		},
	};
	const table = useReactTable({
		data: tableData,
		columns: selection ? [selectionColumn(selectionColumnWidth, selectionHeaderClassname), ...columns] : columns,
		enableSorting: sortable,
		...(enableRowSelection != null ? { enableRowSelection } : {}),
		getRowId: getRowId ?? defaultGetRowId,
		getCoreRowModel: getCoreRowModel(),
		...(manualPagination ? {} : { getSortedRowModel: getSortedRowModel() }),
		...(manualFiltering ? {} : { getFilteredRowModel: getFilteredRowModel() }),
		...(pagination && !manualPagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
		...(manualPagination ? { manualPagination: true, pageCount, rowCount, autoResetPageIndex: autoResetPageIndex ?? false } : {}),
		...(manualFiltering ? { manualFiltering: true } : {}),
		initialState: state,
		...(controlledState ? { state: controlledState, onPaginationChange } : {}),
		meta,
	});

	return table;
};

export default useAdvanceTable;
